import React, { useState, useEffect } from "react";
import SidebarHeader from "./SidebarHeader";
import SidebarSearch from "./SidebarSearch";
import ChannelItem from "./ChannelItem";
import KeywordsSection from "./KeywordsSection";
import SidebarFooter from "./SidebarFooter";
import ChannelSettingsModal from "./ChannelSettingsModal";
import { BookOpen } from "lucide-react";
import axios from "axios";

const TeamsSidebar = ({
  isDarkMode,
  toggleTheme,
  setIsDarkMode,
  channels,
  setChannels,
  activeChannels,
  setActiveChannels,
  activeKeywords,
  toggleKeyword,
  searchQuery,
  setSearchQuery,
  keywordCounts,
  channelMessageCounts,
  API_BASE_URL,
  isMobile,
  closeSidebar,
  onDocumentationClick
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("channels");
  const [keywordSectionHeight, setKeywordSectionHeight] = useState("max-h-96");

  useEffect(() => {
    const storedActiveChannels = JSON.parse(localStorage.getItem("activeChannels"));
    if (storedActiveChannels) setActiveChannels(storedActiveChannels);
  }, [setActiveChannels]);

  // Dynamic height calculation based on window size
  useEffect(() => {
    const calculateKeywordHeight = () => {
      const windowHeight = window.innerHeight;
      const isSmallScreen = windowHeight < 600;
      const isMediumScreen = windowHeight >= 600 && windowHeight < 800;
      const isLargeScreen = windowHeight >= 800;

      if (isSmallScreen) {
        setKeywordSectionHeight("max-h-48"); // 192px for small screens
      } else if (isMediumScreen) {
        setKeywordSectionHeight("max-h-64"); // 256px for medium screens
      } else {
        setKeywordSectionHeight("max-h-96"); // 384px for large screens
      }
    };

    calculateKeywordHeight();
    window.addEventListener('resize', calculateKeywordHeight);
    
    return () => window.removeEventListener('resize', calculateKeywordHeight);
  }, []);

  const handleToggleChannel = (channelId) => {
    setActiveChannels((prev) => {
      const updatedChannels = { ...prev, [channelId]: !prev[channelId] };
      localStorage.setItem("activeChannels", JSON.stringify(updatedChannels));
      return updatedChannels;
    });
  };

  const handleSettingsClick = (channel) => {
    setSelectedChannel(channel);
    setIsSettingsOpen(true);
  };

  const handleSave = async (channelId, updatedChannel) => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/channel/${channelId}`, updatedChannel);
      if (response.data) {
        setChannels((prevChannels) => ({
          ...prevChannels,
          [channelId]: { ...prevChannels[channelId], ...updatedChannel },
        }));
      }
      setIsSettingsOpen(false);
    } catch (error) {
      console.error("Error updating channel:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeywordClick = (keyword) => toggleKeyword(keyword);

  return (
    <div
      className={`flex flex-col h-full max-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"
      } ${isMobile ? 'w-full' : 'w-72'}`}
    >
      <SidebarHeader 
        isDarkMode={isDarkMode} 
        toggleTheme={toggleTheme}
        isMobile={isMobile}
        closeSidebar={closeSidebar}
        edgeServerEndpoint={API_BASE_URL}
      />
      <SidebarSearch 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery} 
        isDarkMode={isDarkMode}
      />

      {/* Tab Navigation */}
      <div className="flex border-b px-4 sm:px-6">
        <button
          className={`flex-1 py-2 text-sm font-medium ${
            activeTab === "channels"
              ? isDarkMode
                ? "text-white border-b-2 border-blue-500"
                : "text-gray-900 border-b-2 border-blue-500"
              : isDarkMode
              ? "text-gray-400 hover:text-white"
              : "text-gray-500 hover:text-gray-900"
          }`}
          onClick={() => setActiveTab("channels")}
        >
          Channels
        </button>
      </div>

      {/* Tab Content */}
      <div className={`flex-grow overflow-hidden px-4 py-2 pb-6 ${isDarkMode ? "dark-mode-scrollbar" : ""}`}>
        {activeTab === "channels" && (
          <div className="space-y-2 mb-6 overflow-y-auto max-h-60">
            {Object.entries(channels).map(([channelId, channel]) => (
              <ChannelItem
                key={channelId}
                channel={channel}
                isActive={activeChannels[channelId]}
                channelMessageCounts={channelMessageCounts}
                isDarkMode={isDarkMode}
                handleToggleChannel={() => handleToggleChannel(channelId)}
                handleSettingsClick={handleSettingsClick}
                edgeServerEndpoint={API_BASE_URL}
              />
            ))}
          </div>
        )}

        {/* Keywords Section - always visible below channels */}
        <div className="flex-1 min-h-0">
          <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Keyword Tracker
          </h3>
          <KeywordsSection
            keywordCounts={keywordCounts}
            activeKeywords={activeKeywords}
            handleKeywordClick={handleKeywordClick}
            isDarkMode={isDarkMode}
            maxHeightClass={keywordSectionHeight}
          />
        </div>

        
      </div>

      <SidebarFooter activeChannels={activeChannels} isDarkMode={isDarkMode} />
      <ChannelSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        channel={selectedChannel}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default TeamsSidebar;